import { batch, observable, observe } from '@legendapp/state'
import { Show, useObservable, useSelector } from '@legendapp/state/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { fetchQRImages, fetchQRListing } from '~features/qr/api'
import { GenerateQRDialog } from '~features/qr/components/GenerateQRDialog'
import { COLUMNS } from '~features/qr/constant'
import { Root } from '~features/qr/types'
import { Button, Card, CardContent } from '~in-app/components'
import { FilterDropdownProps, TableContextProvider, TableState$ } from '~in-app/components/table/TableContext'
import { useAbortController } from '~in-app/lib/hooks'

export const Route = createFileRoute('/console/qr/list')({
  async loader({ abortController, context }) {
    return {
      defaultQRListing: await fetchQRListing({
        abortCtrl: abortController,
        query: {
          sort: (defaultState$.sortList.get() || []).map(item => `${item.id},${item.desc ? 'desc' : 'asc'}`).join(','),
        },
      }),
    }
  },
  component: QRListingPage,
  staticData: {
    privileges: ['0'],
  },
})

const defaultState$ = observable<{
  sortList: { id: string, desc: boolean }[]
  pagination: number
  pageSize: number
  isLinked?: any
  siteIds: string[]
}>({
  sortList: [{ id: 'qrId', desc: true }],
  pagination: 0,
  pageSize: 20,
  isLinked: undefined,
  siteIds: [],
})

function QRListingPage() {
  const state$ = useObservable(defaultState$)
  const navigate = Route.useNavigate()
  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
  }, [])

  const { defaultQRListing } = Route.useLoaderData()
  const abortController = useAbortController()

  const stateSelector = useSelector(() => state$.get())

  const { refetch } = useQuery({
    queryKey: ['qr-listing', stateSelector],
    queryFn: async ({ signal }) => {
      const params = {
        isLinked: stateSelector.isLinked,
        siteIds: stateSelector.siteIds,
        sort: (stateSelector.sortList || [])
          .map(item => `${item.id},${item.desc ? 'desc' : 'asc'}`)
          .join(','),
        page: stateSelector.pagination,
        size: stateSelector.pageSize,
      }

      const response = await fetchQRListing({ abortCtrl: signal, query: params })

      tableState$.data.set(response?.list || [])
      tableState$.totalItem.set(response?.totalItem || 0)
      tableState$.totalPage.set(response?.totalPage || 0)

      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }
      return response
    },
    initialData: defaultQRListing,
  })

  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Status',
      type: 'dropdown',
      isMultiSelect: true,
      options: [
        {
          label: 'Linked',
          value: 'true',
        },
        {
          label: 'Non-linked',
          value: 'false',
        },
      ],
      value: new URLSearchParams(window.location.search).get('isLinked') === 'false'
        ? [{
            label: 'Non-linked',
            value: false,
          }]
        : new URLSearchParams(window.location.search).get('isLinked') === 'true'
          ? [{
              label: 'Linked',
              value: true,
            }]
          : undefined,
      result: new URLSearchParams(window.location.search).get('isLinked') === 'false'
        ? [{
            label: 'Non-linked',
            value: false,
          }]
        : new URLSearchParams(window.location.search).get('isLinked') === 'true'
          ? [{
              label: 'Linked',
              value: true,
            }]
          : undefined,
    },
  ]

  const tableState$ = useObservable<TableState$<Root>>(() => ({
    id: 'qr-table',
    isSortable: true,
    data: [],
    columns: COLUMNS,
    hideColumns: ['linked'],
    sort: state$.sortList,
    pagination: state$.pagination.get(),
    pageSize: state$.pageSize.get() ?? 20,
    totalPage: 0,
    totalItem: 0,
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any[]) => {
      const status = value.find(value => value.label === 'Status')
      batch(() => {
        const searchParams: Record<string, string> = {}

        if (status) {
          state$.isLinked.set(status.result.length > 1 ? undefined : status.result.at(0)?.value ?? undefined)
          searchParams.isLinked = status.result.length > 1 ? undefined : status.result.at(0)?.value ?? undefined
        }
        // Navigate with updated params
        navigate({
          search: (prev) => {
            const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

            return updatedSearch
          },
        })
      })
    },
    setSort: (value: any) => state$.sortList.set(value),
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, pageSize: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
    enableRowSelection: true,
    selectedRows: [],
  }))

  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    const linked = new URLSearchParams(window.location.search).get('isLinked') === 'false'
      ? false
      : new URLSearchParams(window.location.search).get('isLinked') === 'true'
        ? true
        : undefined
    state$.isLinked.set(linked)
    const disposer = observe(state$, (e) => {
      if (e.previous !== undefined && e.value !== undefined) {
        refetch()
      }
    }, { immediate: false })

    return disposer
  }, [])

  const handleExportQR = async () => {
    try {
      await fetchQRImages({
        siteIds: (tableState$.selectedRows.get() || [])?.map(item => item.id),
      })
      toast.success('QR images exported successfully')
      // Reset checkboxes after successful export
      tableState$.selectedRows.set([])
    }
    catch (error) {
      toast.error('Failed to export QR images')
    }
  }

  return (
    <>
      <div className="flex items-center justify-between p-2">
        <h1 className="mb-4 ml-4 text-2xl font-bold">QR Management</h1>

        <div className="flex items-center gap-2">
          <GenerateQRDialog />

          <Show if={() => (tableState$.selectedRows?.get()?.length || 0) > 0}>
            {
              () => (
                <Button onClick={handleExportQR}>
                  Export QR Images
                </Button>
              )
            }
          </Show>
        </div>
      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <TableContextProvider state$={tableState$} />
          </CardContent>
        </Card>
      </div>
    </>
  )
}
